import { useEffect, useState } from 'react';
import { Login } from './components/Login';
import { Sidebar } from './components/Sidebar';
import { Toast } from './components/Toast';
import { Topbar } from './components/Topbar';
import { api, ApiError } from './lib/api';
import { canSeeOthers, hasPermission, isPersonalOwner } from './lib/perm';
import { applyTheme } from './lib/theme';
import { Admin } from './views/Admin';
import { Calculadora } from './views/Calculadora';
import { Clientes } from './views/Clientes';
import { Dashboard } from './views/Dashboard';
import { Documentos } from './views/Documentos';
import { EditorFactura } from './views/EditorFactura';
import { FacturasEmitidas } from './views/FacturasEmitidas';
import { FacturasRecibidas } from './views/FacturasRecibidas';
import { Personal } from './views/Personal';
import { Reportes } from './views/Reportes';
import { Tesoreria } from './views/Tesoreria';
import type { PermissionKey, PublicUser, ThemeId } from '@otter/shared';

export type View = 'dash' | 'cli' | 'em' | 're' | 'tes' | 'rep' | 'pric' | 'sig' | 'pf' | 'ad';

const CRUMBS: Record<View, string> = {
  dash: 'dashboard',
  cli: 'clientes',
  em: 'facturas-emitidas',
  re: 'facturas-recibidas',
  tes: 'tesoreria',
  rep: 'reportes',
  pric: 'calculadora',
  sig: 'firmas',
  pf: 'personal',
  ad: 'admin',
};

const VIEW_PERM: Record<View, PermissionKey | null> = {
  dash: 'dashboard.view',
  cli: 'clients.view',
  em: 'invoicesIssued.view',
  re: 'invoicesReceived.view',
  tes: 'treasury.view',
  rep: 'reports.view',
  pric: 'pricing.view',
  sig: 'signatures.view',
  pf: null,
  ad: null,
};

function canEnter(user: PublicUser, view: View): boolean {
  if (view === 'pf') return isPersonalOwner(user);
  if (view === 'ad') {
    return (
      user.isSystemAdmin ||
      hasPermission(user, 'admin.users.manage') ||
      hasPermission(user, 'admin.roles.manage') ||
      hasPermission(user, 'admin.settings.manage') ||
      hasPermission(user, 'admin.issuer.manage') ||
      hasPermission(user, 'admin.series.manage')
    );
  }
  const perm = VIEW_PERM[view];
  return perm === null ? true : hasPermission(user, perm);
}

export type ToastMsg = { msg: string; kind?: 'ok' | 'err' };

export function App() {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('dash');
  const [scope, setScope] = useState<string>('all');
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  const [comerciales, setComerciales] = useState<Array<{ code: string; name: string }>>([]);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<{ user: PublicUser; theme: ThemeId }>('/admin/auth/me');
        applyTheme(res.theme);
        setUser(res.user);
      } catch {
        // Not logged in.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user || !canSeeOthers(user)) return;
    void (async () => {
      try {
        const res = await api<{
          items: Array<{ id: string; code: string; name: string }>;
        }>('/admin/team');
        setComerciales(res.items.filter((u) => u.code !== user.code));
      } catch {
        /* ignore */
      }
    })();
  }, [user]);

  // If the user no longer has permission for the current view (e.g. role
  // changed remotely), kick them back to a view they can use.
  useEffect(() => {
    if (!user) return;
    if (canEnter(user, view)) return;
    const fallback = (Object.keys(VIEW_PERM) as View[]).find((v) => canEnter(user, v));
    setView(fallback ?? 'dash');
    setOpenInvoice(null);
  }, [user, view]);

  function handleLogout() {
    void api('/admin/auth/logout', { method: 'POST' }).then(() => setUser(null));
  }

  function showToast(msg: string, kind: 'ok' | 'err' = 'ok') {
    setToast({ msg, kind });
  }

  function handleApiError(err: unknown) {
    if (err instanceof ApiError) showToast(err.message, 'err');
    else showToast((err as Error).message, 'err');
  }

  if (loading) return <div className="login-screen" />;
  if (!user) return <Login onLoggedIn={setUser} />;

  return (
    <div className={`app${navOpen ? ' nav-open' : ''}`}>
      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
      <Sidebar
        user={user}
        view={view}
        onView={(v) => {
          setView(v);
          setOpenInvoice(null);
          setNavOpen(false);
        }}
      />
      <main className="main">
        <Topbar
          user={user}
          crumb={CRUMBS[view]}
          scope={scope}
          onScope={setScope}
          comerciales={comerciales}
          onLogout={handleLogout}
          onToggleNav={() => setNavOpen((v) => !v)}
        />
        {view === 'dash' && canEnter(user, 'dash') && (
          <Dashboard
            user={user}
            scope={scope}
            onOpenInvoice={(id) => {
              setView('em');
              setOpenInvoice(id);
            }}
          />
        )}
        {view === 'cli' && canEnter(user, 'cli') && (
          <Clientes user={user} scope={scope} onToast={showToast} onError={handleApiError} />
        )}
        {view === 'em' && canEnter(user, 'em') && !openInvoice && (
          <FacturasEmitidas
            user={user}
            scope={scope}
            onOpen={setOpenInvoice}
            onError={handleApiError}
          />
        )}
        {view === 'em' && canEnter(user, 'em') && openInvoice && (
          <EditorFactura
            id={openInvoice}
            isAdmin={user.isSystemAdmin}
            onClose={() => setOpenInvoice(null)}
            onToast={showToast}
            onError={handleApiError}
          />
        )}
        {view === 're' && canEnter(user, 're') && (
          <FacturasRecibidas user={user} scope={scope} onToast={showToast} onError={handleApiError} />
        )}
        {view === 'tes' && canEnter(user, 'tes') && (
          <Tesoreria user={user} scope={scope} onToast={showToast} onError={handleApiError} />
        )}
        {view === 'rep' && canEnter(user, 'rep') && (
          <Reportes user={user} scope={scope} onError={handleApiError} />
        )}
        {view === 'pric' && canEnter(user, 'pric') && (
          <Calculadora
            canInvoice={hasPermission(user, 'invoicesIssued.edit')}
            onToast={showToast}
            onOpenInvoice={(id: string) => {
              setView('em');
              setOpenInvoice(id);
            }}
          />
        )}
        {view === 'sig' && canEnter(user, 'sig') && (
          <Documentos user={user} onToast={showToast} onError={handleApiError} />
        )}
        {view === 'pf' && canEnter(user, 'pf') && (
          <Personal onToast={showToast} onError={handleApiError} />
        )}
        {view === 'ad' && canEnter(user, 'ad') && (
          <Admin user={user} onToast={showToast} onError={handleApiError} />
        )}
      </main>
      {toast && <Toast msg={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </div>
  );
}
